type ClientSessionState = {
  id: string;
  createdAt: number;
  lastTouchedAt: number;
};

type CorrelationState = {
  clientInstanceId: string | null;
  clientSessionState: ClientSessionState | null;
  hasHydratedFromStorage: boolean;
  lastPersistedClientSessionTouchedAt: number | null;
};

const CLIENT_INSTANCE_ID_STORAGE_KEY = 'alo_client_instance_id';
const CLIENT_SESSION_STATE_STORAGE_KEY = 'alo_client_session_state_v1';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_TOUCH_PERSIST_INTERVAL_MS = 60 * 1000;

let hasInitializedCorrelation = false;
const correlationState: CorrelationState = {
  clientInstanceId: null,
  clientSessionState: null,
  hasHydratedFromStorage: false,
  lastPersistedClientSessionTouchedAt: null,
};
const localStorageReadFailureKeys = new Set<string>();
const localStorageWriteFailureKeys = new Set<string>();

// localStorage keeps the client/session identifiers consistent across same-origin tabs.

const logLocalStorageFailureOnce = (
  failureKeys: Set<string>,
  key: string,
  action: 'read' | 'write',
  error: unknown
) => {
  if (failureKeys.has(key)) return;
  failureKeys.add(key);
  console.debug(`Failed to ${action} ${key} ${action === 'read' ? 'from' : 'to'} localStorage:`, error);
};

const safeReadLocalStorage = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    logLocalStorageFailureOnce(localStorageReadFailureKeys, key, 'read', error);
    return null;
  }
};

const safeWriteLocalStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    logLocalStorageFailureOnce(localStorageWriteFailureKeys, key, 'write', error);
  }
};

const createOpaqueId = (prefix: 'cid' | 'sid') => {
  const randomValue =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

  return `${prefix}_${randomValue}`;
};

const parseClientSessionState = (rawSessionState: string | null): ClientSessionState | null => {
  if (!rawSessionState) return null;

  try {
    const parsedSessionState = JSON.parse(rawSessionState) as Partial<ClientSessionState>;

    if (
      typeof parsedSessionState?.id === 'string' &&
      typeof parsedSessionState?.createdAt === 'number' &&
      typeof parsedSessionState?.lastTouchedAt === 'number'
    ) {
      return parsedSessionState as ClientSessionState;
    }
  } catch (error) {
    console.log('Failed to parse client session state:', error);
  }

  return null;
};

const isExpiredClientSessionState = (sessionState: ClientSessionState | null, now = Date.now()) => {
  if (!sessionState) return true;
  return now - sessionState.lastTouchedAt > SESSION_TIMEOUT_MS;
};

const shouldPersistClientSessionTouch = (now: number) => {
  if (correlationState.lastPersistedClientSessionTouchedAt === null) return true;
  return now - correlationState.lastPersistedClientSessionTouchedAt >= SESSION_TOUCH_PERSIST_INTERVAL_MS;
};

const syncClientInstanceIdFromStorage = (rawClientInstanceId: string | null) => {
  if (rawClientInstanceId) {
    correlationState.clientInstanceId = rawClientInstanceId;
  }

  return correlationState.clientInstanceId;
};

const syncClientSessionStateFromStorage = (rawSessionState: string | null) => {
  const persistedSessionState = parseClientSessionState(rawSessionState);
  if (!persistedSessionState) return correlationState.clientSessionState;

  correlationState.lastPersistedClientSessionTouchedAt = persistedSessionState.lastTouchedAt;

  if (
    !correlationState.clientSessionState ||
    persistedSessionState.lastTouchedAt >= correlationState.clientSessionState.lastTouchedAt
  ) {
    correlationState.clientSessionState = persistedSessionState;
  }

  return correlationState.clientSessionState;
};

const hydrateCorrelationStateFromStorage = () => {
  if (correlationState.hasHydratedFromStorage) return;
  correlationState.hasHydratedFromStorage = true;

  syncClientInstanceIdFromStorage(safeReadLocalStorage(CLIENT_INSTANCE_ID_STORAGE_KEY));
  syncClientSessionStateFromStorage(safeReadLocalStorage(CLIENT_SESSION_STATE_STORAGE_KEY));
};

const persistClientInstanceId = (clientInstanceId: string) => {
  correlationState.clientInstanceId = clientInstanceId;
  safeWriteLocalStorage(CLIENT_INSTANCE_ID_STORAGE_KEY, clientInstanceId);
  return clientInstanceId;
};

const persistClientSessionState = (sessionState: ClientSessionState) => {
  correlationState.clientSessionState = sessionState;
  correlationState.lastPersistedClientSessionTouchedAt = sessionState.lastTouchedAt;
  safeWriteLocalStorage(CLIENT_SESSION_STATE_STORAGE_KEY, JSON.stringify(sessionState));
  return sessionState;
};

export const getOrCreateClientInstanceId = () => {
  hydrateCorrelationStateFromStorage();

  if (correlationState.clientInstanceId) {
    return correlationState.clientInstanceId;
  }

  return persistClientInstanceId(createOpaqueId('cid'));
};

export const touchClientSession = () => {
  const now = Date.now();
  hydrateCorrelationStateFromStorage();

  const currentSessionState = correlationState.clientSessionState;
  if (isExpiredClientSessionState(currentSessionState, now)) {
    return persistClientSessionState({
      id: createOpaqueId('sid'),
      createdAt: now,
      lastTouchedAt: now,
    });
  }

  const activeSessionState = currentSessionState as ClientSessionState;
  const nextSessionState: ClientSessionState = {
    ...activeSessionState,
    lastTouchedAt: now,
  };

  correlationState.clientSessionState = nextSessionState;

  if (shouldPersistClientSessionTouch(now)) {
    return persistClientSessionState(nextSessionState);
  }

  return nextSessionState;
};

export const getCorrelationIdentifiers = () => {
  const clientInstanceId = getOrCreateClientInstanceId();
  const sessionState = touchClientSession();

  return {
    clientInstanceId,
    clientSessionId: sessionState.id,
  };
};

export const getCorrelationTelemetryAttributes = () => {
  const { clientInstanceId, clientSessionId } = getCorrelationIdentifiers();

  return {
    aloClientInstanceId: clientInstanceId,
    aloClientSessionId: clientSessionId,
  };
};

export const getCorrelationHeaders = () => {
  const { clientInstanceId, clientSessionId } = getCorrelationIdentifiers();

  return {
    'x-alo-client-instance-id': clientInstanceId,
    'x-alo-client-session-id': clientSessionId,
  };
};

export const initializeCorrelation = () => {
  if (hasInitializedCorrelation) return;
  hasInitializedCorrelation = true;
  getCorrelationIdentifiers();

  const touchIfVisible = () => {
    if (document.visibilityState === 'hidden') return;
    touchClientSession();
  };

  const syncFromStorageEvent = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage) return;

    if (event.key === CLIENT_INSTANCE_ID_STORAGE_KEY) {
      syncClientInstanceIdFromStorage(event.newValue);
      return;
    }

    if (event.key === CLIENT_SESSION_STATE_STORAGE_KEY) {
      syncClientSessionStateFromStorage(event.newValue);
    }
  };

  window.addEventListener('focus', touchIfVisible);
  window.addEventListener('pageshow', touchIfVisible);
  window.addEventListener('storage', syncFromStorageEvent);
  document.addEventListener('visibilitychange', touchIfVisible);
};
